import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";

// Contexts
import { usePayPlan } from "@/PayPlans/context/PayPlans";

// Layouts
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import PacksLoader from "@/Layouts/components/Skeletons/PacksLoader/PacksLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Custom components
import PackSection from "./PackSection";

// MUI
import { Grid } from "@mui/material";

export default function Packs(props) {
	const {
		sectionId = "packs",
		packs = null,
		tabsProps = {
			tabs: ["New Vehicles", "Pre-Owned Vehicles"],
			errors: [],
		},
		emptyError = {
			error: false,
			emptyMessage: "Please add one or more model-based commissions.",
		},
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { useSectionsErrors } = usePayPlan();
	const { getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// States
	const [model, setModel] = useState(null);

	// Error handler
	const errors = useMemo(() => {
		const errorNew = tabsProps.errors?.includes(getErrorKey(sectionId, "new"));
		const errorPreOwned = tabsProps.errors?.includes(getErrorKey(sectionId, "preOwned"));
		return [errorNew, errorPreOwned];
	}, [sectionId, tabsProps.errors, getErrorKey]);

	// Actions handlers
	const handleUpdatePacks = (section, area, data) => {
		const updatedPacks = { ...model?.packs, [area]: data };
		const update = { ...model, packs: updatedPacks };
		setModel(update);
		onActionUpdate?.(section, area, data);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	const emptyModelsError = useMemo(() => {
		if (model?.packs) {
			let error = true;
			for (const [key, value] of Object.entries(model.packs)) {
				if (key && value?.length) {
					error = false;
					break;
				}
			}
			return { ...emptyError, error };
		}
		return emptyError;
	}, [emptyError, model]);

	useEffect(() => {
		emptyModelsError?.error ? addSectionError(sectionId, "emptyModels") : removeSectionError(sectionId, "emptyModels");
	}, [sectionId, emptyModelsError, addSectionError, removeSectionError]);

	useEffect(() => {
		setModel({
			tabsProps,
			packs,
		});

		return () => {
			setModel(null);
		};
	}, [tabsProps, packs]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid mt={2}>
						<TabLoader />
						<PacksLoader buttons />
					</Grid>
				) : (
					!!model?.packs && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={errors}
							content={Object.entries(model.packs)?.map(([key, definitions], index) => (
								<Grid container key={`${key}_${index}`}>
									<Grid item xs={12}>
										<PackSection
											identifier={key}
											packs={definitions || []}
											emptyError={emptyModelsError}
											readOnly={readOnly}
											onActionUpdate={(data) => handleUpdatePacks(sectionId, key, data)}
											onActionValidation={(value) => handleValidation(sectionId, key, value)}
										/>
									</Grid>
								</Grid>
							))}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
Packs.propTypes = {
	sectionId: PropTypes.string,
	packs: PropTypes.object,
	tabsProps: PropTypes.object,
	emptyError: PropTypes.object,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};
